import { useEffect, useState } from 'react';

/**
 * Tracks which of the given section ids is currently in view, so the header can
 * show an accurate active state while scrolling the landing page.
 *
 * Uses a top-biased rootMargin: a section counts as "active" once it reaches
 * the upper third of the viewport, which matches what the reader is looking at
 * far better than plain intersection.
 */
export default function useActiveSection(ids, { enabled = true } = {}) {
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    if (!enabled || typeof IntersectionObserver === 'undefined') {
      setActiveId(null);
      return;
    }

    const elements = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (elements.length === 0) return;

    const visible = new Map();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.set(entry.target.id, entry.intersectionRatio);
          else visible.delete(entry.target.id);
        });

        if (visible.size === 0) return;
        const [topId] = [...visible.entries()].sort((a, b) => b[1] - a[1])[0];
        setActiveId(topId);
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: [0, 0.25, 0.5, 1] },
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [ids, enabled]);

  return activeId;
}
